function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { QueryReportHeaderReact } from 'common/query-builder/query-report-header';
import { EntityType } from 'common/report/util/saved-entities/types';
import { SearchDisplayContext } from 'common/report/util/search/types';
import { HeaderAction, QueryReportHeaderParams } from 'common/query-builder/query-report-header/types';
import { useCanvasApi } from 'apps/canvas/src/api/canvas';
import { buildQueryReportHeaderParams, normalizeCanvasEntity } from 'common/report/util/saved-entities';
import { userDetailsStore } from 'common/report/stores/user-details';
import { ToastType } from 'common/components/toast/types';
import { addToast } from 'common/components/toast-area/toast-manager';
import { getAppUrl, getReportUrl } from 'common/report/util';
import { Canvas } from 'apps/canvas/src/types';
import { useTranslation } from 'common/i18n/hooks';
import { useRequiredCanvasSidenavContext } from 'common/widgets/canvas-sidenav/context';
import { CanvasItemInfo } from 'common/widgets/canvas-sidenav/types';
import { SidenavSectionIndex, SidenavSectionTrackingName } from 'common/widgets/canvas-sidenav/constants';
import { canvasQueryKeyPrefix } from '../react-query';
import { AppState, useCanvasStore } from '../store';
import { Visibility } from '../types';
import { CanvasCRUDSource, getCommonCanvasTrackingProps, TrackingEvent } from '../tracking';
import { COLORS } from '../utils';
import { CanvasFavoriteButton } from './favorite-button';
import styles from './index.module.styl';
import { CustomHeaderAction } from './types';
type CanvasHeaderProps = {
  canvasId: number;
  projectId: number;
  workspaceId: number;
  userId: number;
};
function onError(error: Error) {
  addToast({
    message: error.message,
    type: ToastType.ERROR
  });
}
const presenceSelector = (state: AppState) => {
  var _state$liveblocks$roo;
  return {
    others: state.liveblocks.others,
    me: (_state$liveblocks$roo = state.liveblocks.room) === null || _state$liveblocks$roo === void 0 ? void 0 : _state$liveblocks$roo.getSelf()
  };
};
export function CanvasHeader(props: CanvasHeaderProps) {
  const [forceUpdateState, forceUpdate] = useState(0);
  const canvas = useCanvasStore(store => store.canvas);
  const isViewOnly = useCanvasStore(store => {
    var _store$canvas;
    return !((_store$canvas = store.canvas) !== null && _store$canvas !== void 0 && _store$canvas.can_update_basic);
  });
  const setCanvas = useCanvasStore(store => store.setCanvas);
  const visibility = useCanvasStore(store => store.visibility);
  const setVisibility = useCanvasStore(store => store.setVisibility);
  const {
    others,
    me
  } = useCanvasStore(useShallow(presenceSelector));
  const onlineUsers = others.concat(me!).filter(user => user === null || user === void 0 ? void 0 : user.id).map(user => ({
    id: parseInt(user.id!, 10),
    name: user.info.name || "",
    email: user.info.email as string || "",
    color: COLORS[user.connectionId % COLORS.length]
  }));
  const canvasSidenavContext = useRequiredCanvasSidenavContext();
  const i18n = useTranslation();
  const navigate = useNavigate();
  const {
    getCanvas,
    duplicateCanvas,
    updateCanvas,
    deleteCanvas,
    favoriteCanvas,
    pinCanvas
  } = useCanvasApi({
    projectId: props.projectId,
    workspaceId: props.workspaceId
  });
  const {
    data: canvasData = null,
    isSuccess,
    isFetching
  } = useQuery({
    queryKey: [...canvasQueryKeyPrefix(String(props.canvasId))],
    queryFn: () => getCanvas({
      id: String(props.canvasId)
    }),
    enabled: !!props.canvasId
  });
  const canvasEntity = normalizeCanvasEntity(canvas);

  // Mutation for creating a canvas
  const duplicateMutation = useMutation({
    mutationFn: duplicateCanvas,
    onSuccess: response => {
      canvasSidenavContext.insertCanvasSidenavItem(response, SidenavSectionIndex.Yours, SidenavSectionTrackingName.Yours);
      navigate(getReportUrl("canvas/".concat(response.id.toString()), {
        projectId: props.projectId,
        workspaceId: props.workspaceId
      }));
      mixpanel.track(TrackingEvent.DuplicateCanvas, getCommonCanvasTrackingProps({
        canvas: response
      }));
      addToast({
        message: i18n.t("canvases.actions.duplicate.success"),
        type: ToastType.SUCCESS,
        timeout: 6000
      });
    },
    onError: () => {
      addToast({
        message: i18n.t("canvases.actions.duplicate.fail"),
        type: ToastType.ERROR,
        timeout: 6000
      });
    }
  });

  // Mutation for updating the canvas
  const updateMutation = useMutation({
    mutationFn: updateCanvas,
    onSuccess: response => {
      var _canvasSidenavContext, _canvasSidenavContext2;
      mixpanel.track(TrackingEvent.EditCanvasTitle, _objectSpread(_objectSpread({}, getCommonCanvasTrackingProps({
        canvas: canvas || {}
      })), {}, {
        'new name': response.name
      }));
      setCanvas(response);
      const sections = (_canvasSidenavContext = (_canvasSidenavContext2 = canvasSidenavContext.getSidenavProps()) === null || _canvasSidenavContext2 === void 0 ? void 0 : _canvasSidenavContext2.sections) !== null && _canvasSidenavContext !== void 0 ? _canvasSidenavContext : [];
      canvasSidenavContext.updateCanvasSidenavItem({
        canvas: response as CanvasItemInfo,
        sidenavSections: sections
      });
    },
    onError
  });

  // Mutation for deleting the canvas
  const deleteMutation = useMutation({
    mutationFn: deleteCanvas,
    onSuccess: response => {
      canvasSidenavContext.deleteSidenavItem(response.id);
      mixpanel.track(TrackingEvent.DeleteCanvas, {
        canvas_id: response.id
      });
      // redirect to home
      navigate(getAppUrl("home"));
      addToast({
        message: i18n.t("canvases.actions.delete.success"),
        type: ToastType.SUCCESS,
        timeout: 6000
      });
    },
    onError: () => {
      addToast({
        message: i18n.t("canvases.actions.delete.fail"),
        type: ToastType.ERROR,
        timeout: 6000
      });
    }
  });

  // Mutation for favoriting the canvas
  const favoriteMutation = useMutation({
    mutationFn: favoriteCanvas,
    onSuccess: (_, request) => {
      const newCanvas = _objectSpread(_objectSpread({}, canvas), {}, {
        is_favorited: request.isFavorited
      }) as Canvas;
      setCanvas(newCanvas);
      mixpanel.track(request.isFavorited ? TrackingEvent.Favorited : TrackingEvent.Unfavorited, getCommonCanvasTrackingProps({
        canvas: newCanvas,
        sourceTrackingProps: {
          crudSource: CanvasCRUDSource.Header
        }
      }));
      canvasSidenavContext === null || canvasSidenavContext === void 0 || canvasSidenavContext.updateSidenavSections({
        canvas: newCanvas as CanvasItemInfo
      });
      addToast({
        message: request.isFavorited ? i18n.t("canvases.actions.favorite.success") : i18n.t("canvases.actions.unfavorite.success"),
        type: ToastType.SUCCESS,
        timeout: 6000
      });
    },
    onError: (_, request) => {
      addToast({
        message: request.isFavorited ? i18n.t("canvases.actions.favorite.fail") : i18n.t("canvases.actions.unfavorite.fail"),
        type: ToastType.ERROR,
        timeout: 6000
      });
    }
  });

  // Mutation for pinning the canvas
  const pinMutation = useMutation({
    mutationFn: pinCanvas,
    onSuccess: (_, request) => {
      // Toggle pinned status. "YYYY-MM-DD" format
      const pinnedDate = canvas !== null && canvas !== void 0 && canvas.pinned_date ? null : new Date().toISOString().substring(0, 10);
      const newCanvas = _objectSpread(_objectSpread({}, canvas), {}, {
        pinned_date: pinnedDate
      }) as Canvas;
      setCanvas(newCanvas);
      mixpanel.track(request.isPinned ? TrackingEvent.Pinned : TrackingEvent.Unpinned, getCommonCanvasTrackingProps({
        canvas: newCanvas,
        sourceTrackingProps: {
          crudSource: CanvasCRUDSource.Header
        }
      }));
      canvasSidenavContext === null || canvasSidenavContext === void 0 || canvasSidenavContext.updateSidenavSections({
        canvas: newCanvas as CanvasItemInfo
      });
      addToast({
        message: request.isPinned ? i18n.t("canvases.actions.pin.success") : i18n.t("canvases.actions.unpin.success"),
        type: ToastType.SUCCESS,
        timeout: 6000
      });
    },
    onError: (_, request) => {
      addToast({
        message: request.isPinned ? i18n.t("canvases.actions.pin.fail") : i18n.t("canvases.actions.unpin.fail"),
        type: ToastType.ERROR,
        timeout: 6000
      });
    }
  });
  useEffect(() => {
    const userDetailsSubscriber = () => forceUpdate(forceUpdateState + 1);
    userDetailsStore.subscribe(userDetailsSubscriber);
    return () => {
      userDetailsStore.unsubscribe(userDetailsSubscriber);
    };
  }, []);
  useEffect(() => {
    if (isSuccess && canvasData) {
      setCanvas(canvasData);
    }
  }, [isSuccess, canvasData, setCanvas]);
  const queryReportHeaderParams: QueryReportHeaderParams = useMemo(() => {
    return _objectSpread(_objectSpread({}, buildQueryReportHeaderParams({
      projectId: props.projectId,
      entityType: EntityType.Canvas,
      entity: canvasEntity,
      invalidEntity: !props.canvasId,
      displayContext: SearchDisplayContext.Canvas
    })), {}, {
      headerActionItemsEl: CanvasFavoriteButton({
        isSelected: canvas === null || canvas === void 0 ? void 0 : canvas.is_favorited,
        isLoading: isFetching,
        onClick: () => {
          favoriteMutation.mutate({
            id: String(props.canvasId),
            isFavorited: !(canvas !== null && canvas !== void 0 && canvas.is_favorited)
          });
        },
        i18n
      }),
      displayContext: SearchDisplayContext.Canvas,
      entityType: EntityType.Canvas,
      trackName: "canvas",
      overflowControlItems: [{
        items: [...(canvas !== null && canvas !== void 0 && canvas.can_pin_canvases ? [{
          name: CustomHeaderAction.PinCanvas,
          label: canvas !== null && canvas !== void 0 && canvas.pinned_date ? i18n.t("canvases.actions.unpin.label") : i18n.t("canvases.actions.pin.label"),
          icon: canvas !== null && canvas !== void 0 && canvas.pinned_date ? "filled-pin" : "pin",
          highlightIcon: Boolean(canvas === null || canvas === void 0 ? void 0 : canvas.pinned_date)
        }] : []), ...(!isViewOnly ? [{
          label: i18n.t("canvases.actions.show-hide"),
          icon: "eye-on",
          subSections: [{
            items: [{
              name: CustomHeaderAction.ShowMetricValue,
              label: i18n.t("canvases.actions.show-metric-value"),
              icon: "checkmark",
              hideIcon: !visibility[Visibility.METRIC_VALUE],
              onSelect: () => false
            }, {
              name: CustomHeaderAction.ShowSparkline,
              label: i18n.t("canvases.actions.show-sparkline"),
              icon: "checkmark",
              hideIcon: !visibility[Visibility.SPARKLINE],
              onSelect: () => false
            }]
          }, {
            items: [{
              name: CustomHeaderAction.ShowCorrelation,
              label: i18n.t("canvases.actions.show-correlation"),
              icon: "checkmark",
              hideIcon: !visibility[Visibility.CORRELATION],
              onSelect: () => false
            }]
          }]
        }] : [])]
      }],
      controlItems: [{
        slot: "favoriteButton"
      }],
      hasUnsavedChanges: false,
      invalidMessage: "",
      projectId: props.projectId,
      workspaceId: props.workspaceId,
      userId: props.userId
    });
  }, [userDetailsStore.value, canvasEntity, visibility, isFetching]);
  const handleHeaderActionClick = (ev: CustomEvent) => {
    const actionName = ev.detail.name || ev.detail.value;
    switch (actionName) {
      case HeaderAction.Delete:
        deleteMutation.mutate({
          id: String(props.canvasId)
        });
        break;
      case HeaderAction.Duplicate:
        duplicateMutation.mutate({
          id: String(props.canvasId)
        });
        break;
      case CustomHeaderAction.PinCanvas:
        pinMutation.mutate({
          id: String(props.canvasId),
          isPinned: !(canvas !== null && canvas !== void 0 && canvas.pinned_date)
        });
        break;
      case CustomHeaderAction.ShowMetricValue:
        setVisibility(_objectSpread(_objectSpread({}, visibility), {}, {
          [Visibility.METRIC_VALUE]: !visibility[Visibility.METRIC_VALUE]
        }));
        break;
      case CustomHeaderAction.ShowSparkline:
        setVisibility(_objectSpread(_objectSpread({}, visibility), {}, {
          [Visibility.SPARKLINE]: !visibility[Visibility.SPARKLINE]
        }));
        break;
      case CustomHeaderAction.ShowCorrelation:
        setVisibility(_objectSpread(_objectSpread({}, visibility), {}, {
          [Visibility.CORRELATION]: !visibility[Visibility.CORRELATION]
        }));
        break;
    }
  };
  const handleSave = (ev: CustomEvent<{
    name: string;
  }>) => {
    const newName = ev.detail.name;
    updateMutation.mutate({
      id: String(props.canvasId),
      name: newName
    });
  };
  return <div className={styles.container} data-sentry-component="CanvasHeader" data-sentry-source-file="index.tsx">
      <QueryReportHeaderReact {...queryReportHeaderParams} onlineUsers={onlineUsers} onActionclick={handleHeaderActionClick} onSave={handleSave} data-sentry-element="QueryReportHeaderReact" data-sentry-source-file="index.tsx" />
    </div>;
}